import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import AlacarteCard from "@/components/billing/AlacarteCard";
import {
  ALACARTE_ITEMS,
  CATEGORY_LABELS,
  type AlacarteItem,
} from "@/lib/billing/alacarte";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const CATEGORY_ORDER: AlacarteItem["category"][] = [
  "discovery",
  "communication",
  "verification",
  "events",
  "profile",
];

const AlaCarte = () => {
  const { session } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    const status = params.get("checkout") ?? params.get("alacarte");
    if (status === "cancel") {
      toast.info("Checkout canceled. Your card was not charged.");
    } else if (status === "success") {
      toast.success("Add-on activated. Welcome to the next layer.");
    }
  }, [params]);

  const grouped = useMemo(() => {
    const buckets = new Map<AlacarteItem["category"], AlacarteItem[]>();
    for (const cat of CATEGORY_ORDER) buckets.set(cat, []);
    for (const item of ALACARTE_ITEMS) {
      buckets.get(item.category)?.push(item);
    }
    return buckets;
  }, []);

  const signedIn = !!session;
  const onboarded = !!profile?.onboarding_completed_at;
  const activeApp = profile?.active_app ?? profile?.primary_app ?? undefined;

  const onAuthRequired = () =>
    navigate(`/auth?next=${encodeURIComponent("/a-la-carte")}`);
  const onOnboardingRequired = () => navigate("/onboarding");

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-16">
        <header className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
            À la carte · 18+
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
            Boosts, badges, and one-offs.
          </h1>
          <p className="mt-4 text-muted-foreground">
            Optional add-ons that layer on top of any tier — including the
            free Signal tier. Buy a subscription for ongoing perks, or
            grab a single one-time use. Every safety feature stays free,
            on every tier, always.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            Looking for ongoing tier perks?{" "}
            <Link to="/pricing" className="underline hover:text-foreground">
              See the tier plans →
            </Link>
          </p>
        </header>

        {/* Incognito notice — listed in V2 §21 but ALWAYS free. */}
        <section className="mt-10 mx-auto max-w-3xl rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
          <Badge variant="outline" className="mt-1 shrink-0">
            Free · Safety
          </Badge>
          <div>
            <h2 className="font-semibold">Incognito Mode is free, on every tier.</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Browse without appearing in anyone's "who viewed me" or
              organic discovery feed. You can still match by liking
              first. Toggle it from your account — no purchase, no upsell.
            </p>
          </div>
        </section>

        {CATEGORY_ORDER.map((cat) => {
          const items = grouped.get(cat) ?? [];
          if (items.length === 0) return null;
          return (
            <section key={cat} className="mt-14">
              <div className="flex items-baseline justify-between">
                <h2 className="text-2xl font-semibold tracking-tight">
                  {CATEGORY_LABELS[cat]}
                </h2>
                <span className="text-xs text-muted-foreground">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>
              <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {items.map((item) => (
                  <AlacarteCard
                    key={item.key}
                    item={item}
                    app={activeApp ?? undefined}
                    signedIn={signedIn}
                    onboarded={onboarded}
                    onAuthRequired={onAuthRequired}
                    onOnboardingRequired={onOnboardingRequired}
                  />
                ))}
              </div>
            </section>
          );
        })}

        <footer className="mt-16 max-w-2xl mx-auto text-center text-xs text-muted-foreground space-y-2">
          <p>
            Add-ons activate within seconds of a successful payment.
            One-time purchases expire by time or by use; subscriptions
            renew until you cancel from your account.
          </p>
          <p>
            Questions? Read our{" "}
            <Link to="/terms" className="underline hover:text-foreground">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline hover:text-foreground">
              Privacy Policy
            </Link>
            .
          </p>
        </footer>
      </main>
      <SiteFooter />
    </div>
  );
};

export default AlaCarte;
